/**
 * Creates a default cell renderer that displays "N/A" for empty/null values
 * @param {Object} cell - Cell object from MaterialReactTable
 * @returns {string} Cell value or "N/A"
 */
export const renderDefaultCell = ({ cell }) => {
  const value = cell?.getValue();
  return value === "" || value === null ? "N/A" : value;
};

/**
 * Creates a cell renderer for percentage values
 * @param {Object} cell - Cell object from MaterialReactTable
 * @returns {string} Formatted percentage value
 */
export const renderPercentageCell = ({ cell }) => {
  const value = cell?.getValue();
  return value ? `${value}%` : "0%";
};

/**
 * Creates a centered column configuration
 * @param {string} accessorKey - Column accessor key
 * @param {string} header - Column header text
 * @param {number} size - Column size
 * @param {Function} Cell - Optional custom cell renderer
 * @returns {Object} Column configuration object
 */
export const createCenteredColumn = (accessorKey, header, size = 60, Cell = null) => ({
  accessorKey,
  header,
  size,
  muiTableHeadCellProps: {
    align: "center",
  },
  muiTableBodyCellProps: {
    align: "center",
  },
  ...(Cell && { Cell }),
});

/**
 * Creates a tier column configuration
 * @param {string} accessorKey - Column accessor key
 * @param {string} header - Column header text
 * @returns {Object} Tier column configuration object
 */
export const createTierColumn = (accessorKey, header) =>
  createCenteredColumn(accessorKey, header, 100, renderDefaultCell);

/**
 * Creates dynamic columns from user column definitions
 * @param {Array} dynamicUserColumns - Array of dynamic column definitions
 * @param {Set} existingKeys - Set of existing column accessor keys
 * @returns {Array} Array of column configurations
 */
export const createDynamicColumns = (dynamicUserColumns, existingKeys) => {
  // Column-specific size overrides for longer headers
  const columnSizeOverrides = {
    specialMessageDaniel: 220, // "Special Message (Daniel)" needs more width
    linkedIn: 180,
    emailName: 180,
    emailCompany: 180,
  };

  return dynamicUserColumns
    .filter((opt) => !existingKeys.has(opt.name))
    .map((opt) => ({
      accessorKey: opt.name,
      header: opt.label,
      enableSorting: false,
      size: columnSizeOverrides[opt.name] || 140,
      Cell: renderDefaultCell,
    }));
};

